import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "./ui/button";
import {
  Clock, MapPin, Building2, Tag, LogIn, LogOut, CheckCircle2, AlertTriangle,
  Eye, CalendarRange, CalendarDays, BellRing, UserX, Globe2,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import { formatTimezoneLabel, timezoneFull } from "../lib/timezone";
import ShiftDetailsDialog from "./ShiftDetailsDialog";
import NonWorkingDayBanner from "./NonWorkingDayBanner";
import { useNonWorkingDays } from "../hooks/useNonWorkingDays";

// --- Helpers temps ---
const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const nowHHMM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const browserTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
};
const hhmmToMin = (s) => {
  if (!s || typeof s !== "string") return null;
  const [h, m] = s.split(":").map(Number);
  return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
};

const ECART_SEUIL_MIN = 5;
const POINTAGE_AVANCE_MIN = 60; // « Pointer l'entrée » s'affiche 60 min avant l'heure prévue

async function captureGeoLocation() {
  if (!("geolocation" in navigator)) return { timezone: browserTimezone() };
  const position = await new Promise((resolve) => {
    let done = false;
    const finish = (p) => { if (!done) { done = true; resolve(p); } };
    navigator.geolocation.getCurrentPosition(
      (p) => finish(p),
      () => finish(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
    setTimeout(() => finish(null), 9000);
  });
  const tz = browserTimezone();
  if (!position) return { timezone: tz };
  const { latitude, longitude, accuracy } = position.coords;
  let address = null;
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&accept-language=fr`,
      { headers: { Accept: "application/json" } },
    );
    if (r.ok) { const j = await r.json(); address = j.display_name || null; }
  } catch { /* noop */ }
  return { latitude, longitude, accuracy, timezone: tz, address };
}

const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Plage semaine en cours = lundi → dimanche de la semaine ISO en cours.
// Aligné avec le KPI backend "Shifts cette semaine" (cf. routes/dashboard.py).
const weekRange = (anchor) => {
  const d = new Date(anchor); d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // 0 = lundi … 6 = dimanche
  const start = new Date(d); start.setDate(d.getDate() - dow);
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return { start: fmt(start), end: fmt(end) };
};

// Plage mois = 1er du mois courant → dernier jour du mois courant (mois calendaire).
// Aligné avec le KPI backend "Shifts ce mois" (cf. routes/dashboard.py).
const monthRange = (anchor) => {
  const d = new Date(anchor);
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { start: fmt(start), end: fmt(end) };
};

/**
 * Widget "Mes shifts" — vue collaborateur du tableau de bord.
 * Liste TOUS les shifts à venir (du jour au plus lointain) sur la période sélectionnée (semaine/mois),
 * triés chronologiquement. Affiche le détail complet et un bouton « Consulter ».
 * Le bouton « Pointer l'entrée » apparaît UNIQUEMENT pour le shift du jour et seulement 1h avant le début.
 */
export default function MyUpcomingShifts() {
  const { user } = useAuth();
  const { brands } = useBrands();
  const [shifts, setShifts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [period, setPeriod] = useState("week"); // "week" | "month"
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [tick, setTick] = useState(0); // refresh chaque minute pour activer le bouton Pointer
  const notifiedClockInRef = useRef(new Set());
  const notifiedClockOutRef = useRef(new Set());

  const today = todayISO();
  const range = useMemo(
    () => (period === "week" ? weekRange(new Date()) : monthRange(new Date())),
    [period],
  );

  // Jours non travaillés couvrant la période affichée (tous départements accessibles)
  const { byDate: nwdByDate, items: nwdItems } = useNonWorkingDays({
    startDate: range.start,
    endDate: range.end,
  });

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [shiftsRes, entriesRes, sitesRes, catsRes] = await Promise.all([
        api.get("/shifts", { params: { collaborateur_id: user.id, start_date: range.start, end_date: range.end } }),
        api.get("/timeentries", { params: { collaborateur_id: user.id, start_date: range.start, end_date: range.end } }),
        api.get("/sites").catch(() => ({ data: [] })),
        api.get("/shift-categories").catch(() => ({ data: [] })),
      ]);
      setShifts(shiftsRes.data || []);
      setEntries(entriesRes.data || []);
      setSites(sitesRes.data || []);
      setCategories(catsRes.data || []);
    } catch {
      setShifts([]); setEntries([]);
    }
  }, [user, range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  // Rafraîchissement temps réel : un nouveau shift créé/modifié/supprimé par un admin
  // déclenche un CustomEvent `plania:shifts-updated` côté Planning.jsx, et le widget recharge.
  useEffect(() => {
    const onShiftsUpdated = () => { load(); };
    window.addEventListener("plania:shifts-updated", onShiftsUpdated);
    // Filet de sécurité : poll basse fréquence (60s) pour synchroniser même hors Planning.
    const pollId = setInterval(load, 60_000);
    return () => {
      window.removeEventListener("plania:shifts-updated", onShiftsUpdated);
      clearInterval(pollId);
    };
  }, [load]);

  // Re-render chaque minute pour activer/désactiver le bouton Pointer en fonction de l'heure courante
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Construction des "rows" enrichies + état
  const rows = useMemo(() => {
    const usedEntryIds = new Set();
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const enriched = shifts.map((s) => {
      let entry = entries.find((e) => e.shift_id === s.id);
      if (!entry) {
        entry = entries.find(
          (e) => !usedEntryIds.has(e.id) && e.collaborateur_id === user?.id
            && e.brand_id === s.brand_id && e.date === s.date,
        );
      }
      if (entry) usedEntryIds.add(entry.id);
      const brand = brands.find((b) => b.id === s.brand_id);
      const site = sites.find((x) => x.id === s.site_id);
      const category = categories.find((c) => c.id === s.shift_category_id);

      const plannedStartMin = hhmmToMin(s.start_time);
      const plannedEndMin = hhmmToMin(s.end_time);
      const actualInMin = entry ? hhmmToMin(entry.clock_in) : null;
      const lateMin = actualInMin != null && plannedStartMin != null ? actualInMin - plannedStartMin : null;

      let state = "a_venir";
      if (entry?.clock_in && entry?.clock_out) state = "termine";
      else if (entry?.clock_in) state = "en_cours";
      else if (s.date < today) state = "manque";

      // Absence déclarée par le manager/admin — prime sur toute autre alerte
      // et désactive la possibilité de pointer côté collaborateur.
      // Idem pour les shifts marqués « indisponible » (suite à une demande
      // d'indisponibilité approuvée) : aucune alerte, aucun pointage attendu.
      const absentDeclared =
        s.absence_status === "absent" || s.unavailability_status === "unavailable";

      // Alertes proactives — uniquement pour les shifts du jour
      const isToday = s.date === today;
      const clockInOverdueMin =
        isToday && !absentDeclared && !entry?.clock_in && plannedStartMin != null
          ? nowMin - (plannedStartMin + ECART_SEUIL_MIN)
          : null;
      const clockOutOverdueMin =
        isToday && !absentDeclared && entry?.clock_in && !entry?.clock_out && plannedEndMin != null
          ? nowMin - (plannedEndMin + ECART_SEUIL_MIN)
          : null;
      const clockInOverdue = clockInOverdueMin != null && clockInOverdueMin >= 0;
      const clockOutOverdue = clockOutOverdueMin != null && clockOutOverdueMin >= 0;

      return {
        shift: s, entry, brand, site, category, state, lateMin,
        clockInOverdue, clockOutOverdue, absentDeclared,
        clockInDelayMin: clockInOverdue ? nowMin - plannedStartMin : null,
        clockOutDelayMin: clockOutOverdue ? nowMin - plannedEndMin : null,
      };
    });
    // Tri chronologique : date ASC puis start_time ASC
    return enriched.sort((a, b) => {
      if (a.shift.date !== b.shift.date) return a.shift.date.localeCompare(b.shift.date);
      return (a.shift.start_time || "").localeCompare(b.shift.start_time || "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, entries, brands, sites, categories, today, user, tick]);

  // Toast d'alerte une fois par shift quand le pointage entrée/sortie est en retard.
  useEffect(() => {
    rows.forEach((r) => {
      if (r.clockInOverdue && !notifiedClockInRef.current.has(r.shift.id)) {
        notifiedClockInRef.current.add(r.shift.id);
        toast.warning(
          `Pointage d'entrée en retard (${r.clockInDelayMin} min) — shift de ${r.shift.start_time}`,
          { duration: 8000 },
        );
      }
      if (r.clockOutOverdue && !notifiedClockOutRef.current.has(r.shift.id)) {
        notifiedClockOutRef.current.add(r.shift.id);
        toast.warning(
          `Pointage de sortie en retard (${r.clockOutDelayMin} min) — shift terminait à ${r.shift.end_time}`,
          { duration: 8000 },
        );
      }
    });
  }, [rows]);

  // Le bouton "Pointer l'entrée" doit apparaître uniquement pour le shift du jour,
  // et au plus tôt 60 min avant l'heure de début prévue.
  const canShowClockIn = (row) => {
    if (row.absentDeclared) return false;
    if (row.shift.date !== today) return false;
    if (row.state !== "a_venir") return false;
    const startMin = hhmmToMin(row.shift.start_time);
    if (startMin == null) return false;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return nowMin >= (startMin - POINTAGE_AVANCE_MIN);
  };

  const clockIn = async (row) => {
    setBusyId(row.shift.id);
    try {
      const geo = await captureGeoLocation();
      if (geo.latitude == null) toast.info("Géo-localisation indisponible — pointage enregistré sans position");
      await api.post("/timeentries", {
        collaborateur_id: user.id,
        brand_id: row.shift.brand_id,
        date: today,
        clock_in: nowHHMM(),
        clock_out: null,
        shift_id: row.shift.id,
        ...geo,
      });
      toast.success("Entrée pointée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de pointage");
    } finally { setBusyId(null); }
  };

  const clockOut = async (row) => {
    if (!row.entry?.id) return;
    setBusyId(row.shift.id);
    try {
      const geo = await captureGeoLocation();
      await api.put(`/timeentries/${row.entry.id}/clock_out`, null, {
        params: {
          clock_out: nowHHMM(),
          ...(geo.latitude != null ? { latitude_out: geo.latitude } : {}),
          ...(geo.longitude != null ? { longitude_out: geo.longitude } : {}),
          ...(geo.accuracy != null ? { accuracy_out: geo.accuracy } : {}),
          ...(geo.timezone ? { timezone_out: geo.timezone } : {}),
          ...(geo.address ? { address_out: geo.address } : {}),
        },
      });
      toast.success("Sortie pointée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de pointage");
    } finally { setBusyId(null); }
  };

  const openDetails = (row) => {
    setSelectedShift(row);
    setDetailsOpen(true);
  };

  if (!user?.id) return null;

  return (
    <div className="bg-white border border-zinc-200 p-6 rounded-sm" data-testid="my-upcoming-shifts">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="label-uppercase mb-1">Mon planning</p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            {period === "week" ? "Cette semaine (lun. → dim.)" : "Ce mois"}
          </h2>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            {formatDateFr(range.start)} → {formatDateFr(range.end)} · {rows.length} shift{rows.length > 1 ? "s" : ""}
          </p>
        </div>
        <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-0.5">
          <button
            onClick={() => setPeriod("week")}
            className={`text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5 ${period === "week" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
            data-testid="period-week-btn"
          >
            <CalendarRange size={12} /> Semaine
          </button>
          <button
            onClick={() => setPeriod("month")}
            className={`text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5 ${period === "month" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
            data-testid="period-month-btn"
          >
            <CalendarDays size={12} /> Mois
          </button>
        </div>
      </div>

      {nwdItems.length > 0 && (() => {
        // Regroupe par (date + label) pour éviter les doublons multi-départements
        const seen = new Set();
        const uniques = [];
        for (const e of nwdItems) {
          const k = `${e.date}||${e.label}`;
          if (seen.has(k)) continue;
          seen.add(k);
          uniques.push(e);
        }
        return (
          <div className="mb-4 space-y-1.5" data-testid="my-upcoming-nwd-list">
            <p className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-1">
              Jours non travaillés dans cette période
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {uniques.map((e) => (
                <div key={`${e.date}-${e.label}`} className="flex items-center gap-2">
                  <span className="text-[11px] font-mono text-zinc-600 min-w-[80px]">
                    {formatDateFr(e.date)}
                  </span>
                  <div className="flex-1">
                    <NonWorkingDayBanner
                      entries={[e]}
                      compact
                      testIdPrefix={`my-nwd-${e.date}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500 font-mono py-6 text-center" data-testid="no-upcoming-shifts">
          Aucun shift planifié sur cette période.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const { shift, entry, brand, site, category, state, lateMin, clockInOverdue, clockOutOverdue, clockInDelayMin, clockOutDelayMin, absentDeclared } = row;
            const isBusy = busyId === shift.id;
            const showClockIn = canShowClockIn(row);
            const isToday = shift.date === today;
            const showAlertBorder = absentDeclared || clockInOverdue || clockOutOverdue;
            return (
              <div
                key={shift.id}
                className={`border rounded-sm p-3 flex items-start gap-3 flex-wrap ${
                  showAlertBorder
                    ? "border-red-400 bg-red-50/50 ring-1 ring-red-200"
                    : isToday
                      ? "border-sky-300 bg-sky-50/40"
                      : "border-zinc-200 bg-white"
                }`}
                data-testid={`upcoming-shift-${shift.id}`}
              >
                <span className="h-12 w-1 rounded-sm flex-shrink-0" style={{ backgroundColor: brand?.color || "#0055FF" }} />
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-sm font-semibold text-zinc-900">
                      {formatDateFr(shift.date)}
                    </p>
                    {isToday && (
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 bg-sky-600 text-white rounded-sm tracking-wider">
                        Aujourd&apos;hui
                      </span>
                    )}
                    {state === "en_cours" && (
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm tracking-wider">
                        En cours
                      </span>
                    )}
                    {state === "termine" && (
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-sm tracking-wider inline-flex items-center gap-1">
                        <CheckCircle2 size={10} /> Terminé
                      </span>
                    )}
                    {state === "manque" && !absentDeclared && (
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm tracking-wider inline-flex items-center gap-1">
                        <AlertTriangle size={10} /> Non pointé
                      </span>
                    )}
                    {lateMin != null && lateMin > ECART_SEUIL_MIN && !absentDeclared && (
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm tracking-wider inline-flex items-center gap-1">
                        Retard {lateMin} min
                      </span>
                    )}
                    {absentDeclared && (
                      <span
                        className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-red-600 text-white border border-red-700 rounded-sm tracking-wider inline-flex items-center gap-1"
                        data-testid={`absence-declared-${shift.id}`}
                      >
                        <UserX size={10} />
                        Absence déclarée
                      </span>
                    )}
                    {!absentDeclared && clockInOverdue && (
                      <span
                        className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-red-50 text-red-700 border border-red-300 rounded-sm tracking-wider inline-flex items-center gap-1 animate-pulse"
                        data-testid={`clock-in-overdue-${shift.id}`}
                      >
                        <BellRing size={10} />
                        Pointage entrée requis — +{clockInDelayMin} min
                      </span>
                    )}
                    {!absentDeclared && clockOutOverdue && (
                      <span
                        className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-red-50 text-red-700 border border-red-300 rounded-sm tracking-wider inline-flex items-center gap-1 animate-pulse"
                        data-testid={`clock-out-overdue-${shift.id}`}
                      >
                        <BellRing size={10} />
                        Pointage sortie requis — +{clockOutDelayMin} min
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono text-zinc-700">
                    <p className="inline-flex items-center gap-1.5" data-testid={`shift-hours-${shift.id}`}>
                      <Clock size={11} className="text-zinc-400" /> {shift.start_time} → {shift.end_time}
                      {brand?.timezone && (() => {
                        const tz = formatTimezoneLabel(brand.timezone);
                        return tz.full ? (
                          <span
                            className="inline-flex items-center gap-1 text-zinc-500 ml-1"
                            title={timezoneFull(brand.timezone)}
                            data-testid={`shift-tz-inline-${shift.id}`}
                          >
                            <Globe2 size={10} className="text-zinc-400" /> {tz.full}
                          </span>
                        ) : null;
                      })()}
                    </p>
                    <p className="inline-flex items-center gap-1.5" data-testid={`shift-brand-${shift.id}`}>
                      <Building2 size={11} className="text-zinc-400" /> {brand?.name || "—"}
                    </p>
                    <p className="inline-flex items-center gap-1.5" data-testid={`shift-site-${shift.id}`}>
                      <MapPin size={11} className="text-zinc-400" /> {site?.name || "Site non précisé"}
                    </p>
                    <p className="inline-flex items-center gap-1.5" data-testid={`shift-category-${shift.id}`}>
                      <Tag size={11} className="text-zinc-400" />
                      {category ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color || "#0055FF" }} />
                          {category.name}
                        </span>
                      ) : (
                        <span className="text-zinc-400">Sans catégorie</span>
                      )}
                    </p>
                  </div>

                  {/* Pauses planifiées visibles côté collaborateur */}
                  {Array.isArray(shift.breaks) && shift.breaks.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`shift-breaks-${shift.id}`}>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-amber-700">Pauses :</span>
                      {shift.breaks.map((b, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800"
                          title={b.label || (b.is_paid ? "Rémunérée" : "Non rémunérée")}
                        >
                          {b.start_time}-{b.end_time}
                          {b.label && <span className="text-amber-700">· {b.label}</span>}
                          <span className="text-[9px] text-amber-600">{b.is_paid ? "rém." : "non rém."}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {entry && (
                    <p className="text-xs text-zinc-700 font-mono mt-1.5">
                      Pointage : <span className="font-semibold">{entry.clock_in || "—"}</span>
                      {entry.clock_out
                        ? <> → <span className="font-semibold">{entry.clock_out}</span></>
                        : <> → <span className="text-emerald-600">en cours…</span></>}
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openDetails(row)}
                    className="rounded-sm"
                    data-testid={`consult-shift-btn-${shift.id}`}
                  >
                    <Eye size={12} className="mr-1" /> Consulter
                  </Button>
                  {showClockIn && (
                    <Button
                      onClick={() => clockIn(row)}
                      disabled={isBusy}
                      size="sm"
                      className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm"
                      data-testid={`clock-in-shift-${shift.id}`}
                    >
                      <LogIn size={12} className="mr-1" /> {isBusy ? "…" : "Pointer l'entrée"}
                    </Button>
                  )}
                  {!absentDeclared && state === "en_cours" && isToday && (
                    <Button
                      onClick={() => clockOut(row)}
                      disabled={isBusy}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
                      data-testid={`clock-out-shift-${shift.id}`}
                    >
                      <LogOut size={12} className="mr-1" /> {isBusy ? "…" : "Pointer la sortie"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ShiftDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        shift={selectedShift?.shift}
        brand={selectedShift?.brand}
        site={selectedShift?.site}
        category={selectedShift?.category}
      />

      {/* tick consumer to keep eslint happy when re-rendering every minute */}
      <span className="hidden" data-testid="tick" data-tick={tick} />
    </div>
  );
}
